import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Coins,
  ExternalLink,
  Loader2,
  PlusCircle,
  Search,
  Wallet,
  Users as UsersIcon,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useToast } from '../../components/ui/toast';
import {
  useAdjustCredits,
  useAdminBilling,
  useAdminUserBilling,
  formatEUR,
  type AdminBillingRow,
} from '../../hooks/useBilling';
import { HeaderActions } from '../../components/HeaderActions';

// ─────────────────────────────────────────────────────────────────────
// /admin/billing — overview
// ─────────────────────────────────────────────────────────────────────

export function AdminBillingOverviewPage() {
  const billing = useAdminBilling();
  const [search, setSearch] = React.useState('');

  const filtered = React.useMemo(() => {
    const items = billing.data?.items ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const haystack = [it.email, it.displayName, it.firstName, it.lastName, it.uid]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [billing.data, search]);

  return (
    <div className="space-y-6">
      <HeaderActions>
        <div className="flex items-center gap-2">
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Suchen …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-56"
          />
        </div>
      </HeaderActions>

      <div className="grid gap-3 sm:grid-cols-4">
        <SummaryCard
          icon={<UsersIcon className="h-4 w-4" />}
          label="Aktive Kunden"
          value={(billing.data?.totals.activeUsers ?? 0).toString()}
          hint={`${billing.data?.totals.users ?? 0} Konten gesamt`}
        />
        <SummaryCard
          icon={<Coins className="h-4 w-4" />}
          label="Abo-Kunden"
          value={(billing.data?.totals.subscriptionUsers ?? 0).toString()}
        />
        <SummaryCard
          icon={<Wallet className="h-4 w-4" />}
          label="PAYG-Kunden"
          value={(billing.data?.totals.paygUsers ?? 0).toString()}
        />
        <SummaryCard
          icon={<Coins className="h-4 w-4" />}
          label="Verfügbares Guthaben"
          value={formatEUR(billing.data?.totals.totalCreditCents ?? 0)}
          hint="Summe über alle Wallets"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Abrechnung pro Kunde</CardTitle>
          <CardDescription>
            {billing.isLoading
              ? 'Lade …'
              : `${filtered.length} von ${billing.data?.items.length ?? 0} Konten`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {billing.isLoading ? (
            <div className="flex h-32 items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade …
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kunde</TableHead>
                  <TableHead>Modus</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Guthaben</TableHead>
                  <TableHead>Mollie</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((u) => (
                  <UserRow key={u.uid} u={u} />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function UserRow({ u }: { u: AdminBillingRow }) {
  const b = u.billing;
  return (
    <TableRow>
      <TableCell>
        <Link to={`/admin/billing/${u.uid}`} className="flex flex-col hover:underline">
          <span className="font-medium">
            {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.displayName || '—'}
          </span>
          <span className="text-xs text-muted-foreground">{u.email}</span>
        </Link>
      </TableCell>
      <TableCell>
        {b.mode ? (
          <Badge variant="outline">{b.mode === 'subscription' ? 'Abo' : 'PAYG'}</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell>
        <Badge
          variant={
            b.status === 'active'
              ? 'success'
              : b.status === 'pending'
                ? 'warning'
                : b.status === 'cancelled'
                  ? 'muted'
                  : 'destructive'
          }
        >
          {String(b.status)}
        </Badge>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatEUR(b.creditCents ?? 0)}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {b.mollieCustomerId ? (
          <a
            href={`https://www.mollie.com/dashboard/customers/${b.mollieCustomerId}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center hover:underline"
          >
            <code className="font-mono">{String(b.mollieCustomerId).slice(0, 12)}…</code>
            <ExternalLink className="ml-1 h-3 w-3" />
          </a>
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell className="text-right">
        <Button asChild variant="ghost" size="sm" className="h-7 text-xs">
          <Link to={`/admin/billing/${u.uid}`}>Detail</Link>
        </Button>
      </TableCell>
    </TableRow>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardDescription>{label}</CardDescription>
        <span className="text-muted-foreground">{icon}</span>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// /admin/billing/:uid — per-user detail
// ─────────────────────────────────────────────────────────────────────

export function AdminBillingDetailPage() {
  const { uid } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const detail = useAdminUserBilling(uid);
  const [adjustOpen, setAdjustOpen] = React.useState(false);

  if (!uid) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
        Keine Benutzer-ID übergeben.
      </div>
    );
  }
  if (detail.isLoading) {
    return (
      <div className="flex h-32 items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade …
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
        Fehler: {(detail.error as Error)?.message ?? 'Konnte Daten nicht laden.'}
      </div>
    );
  }

  const data = detail.data;
  const b = data.billing as Record<string, unknown>;

  return (
    <div className="space-y-6">
      <HeaderActions>
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/billing')}>
          <ArrowLeft className="mr-1.5 h-4 w-4" /> Zurück
        </Button>
        <Button size="sm" onClick={() => setAdjustOpen(true)}>
          <PlusCircle className="mr-1.5 h-3.5 w-3.5" /> Korrekturbuchung
        </Button>
      </HeaderActions>

      <Card>
        <CardHeader>
          <CardTitle>
            {[data.user.firstName, data.user.lastName].filter(Boolean).join(' ') ||
              data.user.displayName ||
              data.user.email}
          </CardTitle>
          <CardDescription>{data.user.email}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Modus" value={b.mode ? (b.mode === 'subscription' ? 'Abo' : 'PAYG') : '—'} />
            <Stat label="Status" value={String(b.status ?? '—')} />
            <Stat
              label="Guthaben"
              value={formatEUR((b.creditCents as number) ?? 0)}
              tone="primary"
            />
            <Stat
              label="Letzte Belastung"
              value={(b.lastChargeAt as string | null)?.slice(0, 10) ?? '—'}
            />
          </div>
          {data.address && (
            <div className="mt-4 rounded-md border border-border p-3 text-xs">
              <div className="mb-1 font-medium uppercase tracking-wider text-muted-foreground">
                Rechnungsadresse
              </div>
              <div>
                {String(data.address.companyName ?? '')}{' '}
                {data.address.vatId ? (
                  <span className="text-muted-foreground">· UID: {String(data.address.vatId)}</span>
                ) : null}
              </div>
              <div className="text-muted-foreground">
                {String(data.address.line1 ?? '')}, {String(data.address.postalCode ?? '')}{' '}
                {String(data.address.city ?? '')} ({String(data.address.country ?? '')})
              </div>
            </div>
          )}
          {Boolean(b.mollieCustomerId) && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-muted-foreground">Mollie:</span>
              <a
                href={`https://www.mollie.com/dashboard/customers/${b.mollieCustomerId}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center text-primary hover:underline"
              >
                <code className="font-mono">{String(b.mollieCustomerId)}</code>
                <ExternalLink className="ml-1 h-3 w-3" />
              </a>
              {b.mollieSubscriptionId ? (
                <span className="text-muted-foreground">
                  · Sub: <code className="font-mono">{String(b.mollieSubscriptionId)}</code>
                </span>
              ) : null}
              {b.mollieMandateId ? (
                <span className="text-muted-foreground">
                  · Mandat: <code className="font-mono">{String(b.mollieMandateId)}</code>
                </span>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      {data.assignedNumbers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Zugewiesene Nummer(n)</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nummer</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Zugewiesen am</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.assignedNumbers.map((n) => (
                  <TableRow key={n.id}>
                    <TableCell className="font-mono">{n.e164}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{n.provider}</Badge>
                    </TableCell>
                    <TableCell>{n.status}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {n.assignedAt ? new Date(n.assignedAt).toLocaleString('de-DE') : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Buchungs-Historie</CardTitle>
          <CardDescription>
            Append-only Audit-Trail. Jede Aufladung, Abbuchung, Korrektur ist hier.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.transactions.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Noch keine Buchungen.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Datum</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead className="text-right">Betrag</TableHead>
                  <TableHead className="text-right">Saldo nach</TableHead>
                  <TableHead className="text-right">MwSt</TableHead>
                  <TableHead>Grund</TableHead>
                  <TableHead>Mollie</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.transactions.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="text-xs">
                      {t.ts ? new Date(t.ts).toLocaleString('de-DE') : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={t.amountCents >= 0 ? 'success' : 'muted'}>
                        {t.type}
                      </Badge>
                    </TableCell>
                    <TableCell
                      className={`text-right tabular-nums ${
                        t.amountCents >= 0 ? 'text-emerald-400' : 'text-destructive'
                      }`}
                    >
                      {t.amountCents >= 0 ? '+' : ''}
                      {formatEUR(t.amountCents)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatEUR(t.balanceAfterCents)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {t.vatCents != null ? formatEUR(t.vatCents) : '—'}
                      {t.vatRate != null && (
                        <span className="ml-1 text-muted-foreground">
                          ({(t.vatRate * 100).toFixed(0)}%)
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{t.reason}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {t.mollie?.paymentId ? (
                        <code className="font-mono">{t.mollie.paymentId.slice(0, 12)}…</code>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {adjustOpen && (
        <AdjustDialog uid={uid} onClose={() => setAdjustOpen(false)} />
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'primary';
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div
        className={`mt-1 text-2xl font-semibold tabular-nums ${
          tone === 'primary' ? 'text-primary' : ''
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function AdjustDialog({ uid, onClose }: { uid: string; onClose: () => void }) {
  const adjust = useAdjustCredits();
  const { toast } = useToast();
  const [amountEur, setAmountEur] = React.useState<string>('0.00');
  const [reason, setReason] = React.useState('');
  const [type, setType] = React.useState<'admin_adjustment' | 'signup_bonus' | 'refund'>(
    'admin_adjustment',
  );

  async function onSubmit() {
    const cents = Math.round(parseFloat(amountEur.replace(',', '.')) * 100);
    if (!Number.isFinite(cents) || cents === 0) {
      toast({ title: 'Betrag fehlt', variant: 'destructive' });
      return;
    }
    if (!reason.trim()) {
      toast({ title: 'Grund pflicht', variant: 'destructive' });
      return;
    }
    try {
      const r = await adjust.mutateAsync({ uid, amountCents: cents, reason: reason.trim(), type });
      toast({
        title: 'Korrektur gebucht',
        description: `Neuer Saldo: ${formatEUR(r.balanceAfterCents)}`,
      });
      onClose();
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Korrekturbuchung</DialogTitle>
          <DialogDescription>
            Manuelle ± Anpassung des Guthabens. Erscheint sofort in der Buchungs-Historie.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Typ</Label>
            <Select value={type} onValueChange={(v) => setType(v as typeof type)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin_adjustment">Admin-Korrektur</SelectItem>
                <SelectItem value="signup_bonus">Willkommens-Bonus</SelectItem>
                <SelectItem value="refund">Erstattung</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Betrag (EUR, ± erlaubt)</Label>
            <Input
              type="text"
              value={amountEur}
              onChange={(e) => setAmountEur(e.target.value)}
              placeholder="z.B. 10.00 oder -5.00"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Grund (pflicht für Audit)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="z.B. Goodwill nach Ausfall vom 12.04."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={() => void onSubmit()} disabled={adjust.isPending}>
            {adjust.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Buchen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
